import { Paths } from "../../Constants/paths";
import { socket } from "../../socket";

const LOGOUT_NOTICE_KEY = "authLogoutNotice";

// Fired when the session is dropped while the login page is already mounted,
// so it can pick up the notice without a page reload.
export const LOGOUT_NOTICE_EVENT = "auth:logout-notice";

let cachedNotice: string | null = null;
let hasReadStoredNotice = false;
let isLoggingOut = false;

const readSessionStorage = (key: string): string | null => {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeSessionStorage = (key: string, value: string | null) => {
  try {
    if (value === null) {
      window.sessionStorage.removeItem(key);
      return;
    }
    window.sessionStorage.setItem(key, value);
  } catch {
    // Storage can be unavailable in private browsing modes.
  }
};

export const setLogoutNotice = (message: string | null) => {
  cachedNotice = message;
  hasReadStoredNotice = true;
  writeSessionStorage(LOGOUT_NOTICE_KEY, message);
};

// The notice has to survive the full page load caused by the redirect, but the
// value is cached in memory so repeated reads (StrictMode, re-renders) are safe.
export const readLogoutNotice = (): string | null => {
  if (!hasReadStoredNotice) {
    hasReadStoredNotice = true;
    cachedNotice = readSessionStorage(LOGOUT_NOTICE_KEY);
    writeSessionStorage(LOGOUT_NOTICE_KEY, null);
  }
  return cachedNotice;
};

export const dismissLogoutNotice = () => {
  cachedNotice = null;
  writeSessionStorage(LOGOUT_NOTICE_KEY, null);
};

export const clearAuthSession = () => {
  try {
    window.localStorage.clear();
  } catch {
    // Ignore storage failures, the redirect below still ends the session.
  }
  socket.auth = { token: null };
  socket.disconnect();
};

const isOnLoginPage = () =>
  window.location.pathname.replace(/\/+$/, "") === Paths.Login;

export const forceLogout = ({
  message = null,
}: { message?: string | null } = {}) => {
  if (isLoggingOut) return;
  isLoggingOut = true;

  setLogoutNotice(message);
  clearAuthSession();

  if (isOnLoginPage()) {
    isLoggingOut = false;
    window.dispatchEvent(new Event(LOGOUT_NOTICE_EVENT));
    return;
  }

  window.location.replace(Paths.Login);
};
